/**
 * Word-wrap text for canvas rendering.
 * Splits text into lines that fit within maxWidth, breaking only between words.
 * A single word that exceeds maxWidth is kept on its own line (never broken mid-word).
 */
export function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  if (!text) return [''];

  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const metrics = ctx.measureText(testLine);

    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.length > 0 ? lines : [''];
}

/**
 * Draw word-wrapped text on a canvas with stroke and fill.
 * Text is drawn centered around (0, 0) after translate/rotate has been applied.
 */
export function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  fontSize: number,
  maxWidth: number,
  options: {
    color: string;
    strokeColor: string;
    strokeWidth: number;
  }
) {
  const lines = wrapText(ctx, text, maxWidth);
  const lineHeight = fontSize * 1.15;
  const totalHeight = lines.length * lineHeight;
  const startY = -totalHeight / 2 + lineHeight / 2;

  for (let i = 0; i < lines.length; i++) {
    const y = startY + i * lineHeight;

    if (options.strokeWidth > 0) {
      ctx.strokeStyle = options.strokeColor;
      ctx.lineWidth = options.strokeWidth * 2;
      ctx.lineJoin = 'round';
      ctx.strokeText(lines[i], 0, y);
    }

    ctx.fillStyle = options.color;
    ctx.fillText(lines[i], 0, y);
  }
}
